import { Injectable } from '@angular/core';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { C2cSourceCardService } from './c2c-source-card.service';
import { convertNonEnglishDigits } from '@digipay/strings';
import {
  Bank,
  BankCard,
  BankCardApiService,
  BlankCard,
  CardDetailInterface,
  CardPanDto,
  CardPin,
  CardProfile,
  CardType,
  CardZonesEnum,
  DynamicPasswordRequest,
  SearchCardsResponseInterface,
} from '@client-monorepo/daily-fintech/bank-card';
import { ShaparakService } from './shaparak.service';
import { PayClientApiService, PaymentResultInterface } from '@client-monorepo/payment/purchase';
import { JSEncrypt } from 'jsencrypt';
import { CardVerificationService } from './card-verification.service';
import { C2cApiService } from './c2c-api.service';
import { CardCheckResponse } from '../models/card-check-response.interface';
import { CardDefinition } from '../models/card-definition.interface';
import { CheckCardRequest } from '../models/check-card-request.interface';
import {
  convertStoredCardToCardProfile,
  getCardPanPostfix,
  getCardPanPrefix,
  MessageService,
  StorageService,
} from '@client-monorepo/common/utilities';
import { CardVerifyData } from '../models/card-verify-data';
import { CARD_TRANSACTION_TYPES } from '../models/card-transaction-types.enum';
import { CardToCardRequest } from '../models/card-to-card-request.interface';
import { CardPaymentConfig } from '../models/card-payment-config.interface';
import { CardRegistration } from '../models/card-registration.enum';
import { CardToCardRecommendationResponse, Recommendation } from '../models/card-to-card-recommendation-response';
import { CardRecommendationConfigResponse } from '../models/card-recommendation-config-response';
import { CardPaymentConfigResponse } from '../models/card-payment-config-response';
import { C2cEcdhService } from './c2c-ecdh.service';
import { CardEditBottomSheetComponent } from '../../components/card-edit-bottom-sheet/card-edit-bottom-sheet.component';
import { IndirectDeletingRecommendationComponent } from '../../components/indirect-deleting-recommendation/indirect-deleting-recommendation.component';
import { SOURCE_CARD_LOAD_STATUS } from '../models/source-card-load-status';
import { NgxBottomSheetService } from '@digipay/ngx-bottom-sheet';

@Injectable({
  providedIn: 'root',
})
export class C2cService {
  allBanks: BehaviorSubject<Bank[]> = new BehaviorSubject<Bank[]>([]);

  /*
 |--------------------------------------------------------------------------
 | Source
 |--------------------------------------------------------------------------
 | These group of variables are meant for holding the source card
 | details
 */
  sourceBank = new BehaviorSubject<Bank | null>(null);

  sourceStoredCardsList: BehaviorSubject<BankCard[]> = new BehaviorSubject<BankCard[]>([]);

  sourceStoredCard: BehaviorSubject<BankCard | null> = new BehaviorSubject<BankCard | null>(null);

  sourceCardsLoaded: ReplaySubject<SOURCE_CARD_LOAD_STATUS> = new ReplaySubject<SOURCE_CARD_LOAD_STATUS>(1);

  loadedSourceCardList: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  detectingSourceStep: BehaviorSubject<'new' | 'stored' | ''> = new BehaviorSubject<'new' | 'stored' | ''>('');

  sourceCardNumber: BehaviorSubject<string> = new BehaviorSubject<string>('');

  /*
  |--------------------------------------------------------------------------
  | Destination
  |--------------------------------------------------------------------------
  | These group of variables are meant for holding the destination (target)
  | card details
  */
  destBank: BehaviorSubject<Bank | null> = new BehaviorSubject<Bank | null>(null);

  destCardProfile: BehaviorSubject<CardProfile | null> = new BehaviorSubject<CardProfile | null>(null);

  destStoredCardsList: BehaviorSubject<BankCard[]> = new BehaviorSubject<BankCard[]>([]);

  destStoredCardsListBeforeDelete: BehaviorSubject<BankCard[]> = new BehaviorSubject<BankCard[]>([]);

  destStoredCardsHasNext: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false); // todo check default value

  destStoredCard: BehaviorSubject<BankCard | null> = new BehaviorSubject<BankCard | null>(null);

  destinationCardsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  destCardNumber: BehaviorSubject<string> = new BehaviorSubject<string>('');

  /**
   * Blank Cards
   */
  addNewCardUI: BehaviorSubject<BlankCard | null> = new BehaviorSubject<BlankCard | null>(null);

  defaultCardUI: BehaviorSubject<BlankCard | null> = new BehaviorSubject<BlankCard | null>(null);

  /**
   * Certificate file name in order to send to file server API and
   * encrypt card profiles with it.
   */
  profileEncryptionCertFile = '';

  /**
   * Holds the certificate itself (text)
   */
  profileEncryptionCert = '';

  amount: BehaviorSubject<string> = new BehaviorSubject<string>('');

  note: BehaviorSubject<string> = new BehaviorSubject<string>('');

  paymentResult: BehaviorSubject<PaymentResultInterface | null> = new BehaviorSubject<PaymentResultInterface | null>(null);

  /*
  |--------------------------------------------------------------------------
  | Recommendation
  |--------------------------------------------------------------------------
  | These group of variables are meant for  holding the repeated transactions
  */

  // tslint:disable-next-line:max-line-length
  recommendationTransactionsList: BehaviorSubject<CardToCardRecommendationResponse | null> =
    new BehaviorSubject<CardToCardRecommendationResponse | null>(null);

  repeatTransaction: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  transactionMessage: BehaviorSubject<string> = new BehaviorSubject<string>('');

  recommendationConfig: BehaviorSubject<CardRecommendationConfigResponse | null> =
    new BehaviorSubject<CardRecommendationConfigResponse | null>(null);

  // tslint:disable-next-line:max-line-length
  fromRepeatTransaction: BehaviorSubject<{
    repeat: boolean;
    item: Recommendation;
  } | null> = new BehaviorSubject<{
    repeat: boolean;
    item: Recommendation;
  } | null>(null);

  serverTraceCode: BehaviorSubject<string> = new BehaviorSubject<string>('');

  cardPaymentConfig = new BehaviorSubject<CardPaymentConfigResponse | null>(null);

  // In order to determine the necessity of registration in shaparak
  strictModeEditCard = new BehaviorSubject<boolean | false>(false);

  constructor(
    private messageService: MessageService,
    private cardVerificationService: CardVerificationService,
    private c2cEcdhService: C2cEcdhService,
    private storageService: StorageService,
    private shaparakService: ShaparakService,
    private c2cSourceCardService: C2cSourceCardService,
    private bankCardApiService: BankCardApiService,
    private payClientApiService: PayClientApiService,
    private c2cApiService: C2cApiService,
    private bottomSheetService: NgxBottomSheetService,
  ) {}

  removePanDashes(pan: string): string {
    return pan.replace(/-/gi, '');
  }

  findBankByPrefix(prefix: string): Bank | null {
    const card = this.allBanks.getValue().filter((b) => b.cardPrefixes.indexOf(prefix) >= 0);
    if (card.length === 0) {
      return null;
    }
    return card[0];
  }

  getAllBanksCodes(allBanks: Bank[]) {
    return allBanks.map((bank) => bank.code);
  }

  getAllBanks() {
    this.bankCardApiService.getAllBanks().subscribe(
      (banks) => {
        this.c2cApiService.timeout.next(banks.maxTimeout);
        this.allBanks.next(banks.banks);
        this.profileEncryptionCertFile = this.allBanks.getValue()[0].profileCert;
        // make an array of banks codes for searching user's cards

        const allBanksCodes = this.getAllBanksCodes(banks.banks);
        this.getSourceCardsList(allBanksCodes);
      },
      () => {
        this.sourceCardsLoaded.next(SOURCE_CARD_LOAD_STATUS.FAILED);
      },
    );
  }

  getSourceCardsList(availableBanks: string[]) {
    this.loadedSourceCardList.next(false);
    this.bankCardApiService
      .searchCards(
        { type: 'source', serviceType: 0 },
        {
          restrictions: [
            {
              type: 'collection',
              field: 'bankCodes',
              values: availableBanks,
            },
          ],
        },
      )
      .subscribe(
        (res) => {
          this.sourceStoredCardsList.next(res.cards);
          this.addNewCardUI.next(res.newCard);
          this.defaultCardUI.next(res.defaultCard);
          this.sourceCardsLoaded.next(SOURCE_CARD_LOAD_STATUS.SUCCESS);
          this.loadedSourceCardList.next(true);
        },
        () => {
          this.sourceCardsLoaded.next(SOURCE_CARD_LOAD_STATUS.FAILED);
          this.loadedSourceCardList.next(false);
        },
      );
  }

  removeSourceCardFromLocalData(card: BankCard): void {
    let list = this.sourceStoredCardsList.getValue();
    list = list.filter((c) => c.cardIndex !== card.cardIndex);
    this.sourceStoredCardsList.next(list);
  }

  removeDestCardFromLocalData(card: BankCard): void {
    this.destStoredCardsListBeforeDelete.next(this.destStoredCardsList.getValue());
    let list = this.destStoredCardsList.getValue();
    list = list.filter((c) => c.cardIndex !== card.cardIndex);
    this.destStoredCardsList.next(list);
  }

  /**
   * Runs when user enter the last number of card
   */

  getCardProfileForUser(cardNumber: string): Promise<CardProfile> {
    return new Promise((resolve, reject) => {
      this.payClientApiService.getCertFile(this.profileEncryptionCertFile).subscribe((certText) => {
        this.profileEncryptionCert = certText;

        const encrypt = new JSEncrypt();
        encrypt.setPublicKey(certText);
        const encryptedPan = encrypt.encrypt(cardNumber) as string;

        this.getCardProfile(encryptedPan).then(
          (res) => {
            res.pan = cardNumber;
            resolve(res);
          },
          (error) => {
            reject(error);
          },
        );
      });
    });
  }

  getCardProfile(encryptedPan: string): Promise<CardProfile> {
    return new Promise((resolve, reject) => {
      this.bankCardApiService
        .getCardProfile({
          pan: {
            value: encryptedPan,
            expireDate: undefined,
            type: '1',
          },
          certFile: this.profileEncryptionCertFile,
        })
        .subscribe(
          (data) => {
            resolve(data);
          },
          (e) => {
            reject(e);
          },
        );
    });
  }

  // ----------------------------------
  getDestinationCardsList(page = 0) {
    this.destinationCardsLoading.next(true);
    this.bankCardApiService.searchCards({ type: 'target', page, size: 20 }).subscribe({
      next: (cards: SearchCardsResponseInterface) => {
        this.destStoredCardsList.next([...(this.destStoredCardsList.getValue() || []), ...cards.cards]);
        this.destStoredCardsHasNext.next(cards.hasNext);
      },
      complete: () => {
        this.destinationCardsLoading.next(false);
      },
    });
  }

  checkKyc(): Promise<CardCheckResponse> {
    const amount = Number(this.amount.getValue());
    let request: CheckCardRequest;

    if (this.sourceStoredCard.getValue()) {
      request = {
        amount,
        prefix: this.sourceStoredCard.getValue()?.prefix as string,
        postfix: this.sourceStoredCard.getValue()?.postfix as string,
        cardIndex: this.sourceStoredCard.getValue()?.cardIndex,
        panType: CardType.INDEX,
      };
    } else {
      request = {
        prefix: getCardPanPrefix(this.c2cSourceCardService.sourceCardProfile.getValue()?.pan as string),
        postfix: getCardPanPostfix(this.c2cSourceCardService.sourceCardProfile.getValue()?.pan as string),
        cardIndex: this.c2cSourceCardService.sourceCardProfile.getValue()?.cardIndex,
        panType: CardType.ENCRYPTED,
        amount,
      };
    }

    return new Promise((resolve, reject) => {
      this.c2cApiService.checkCardTransferKyc(request).subscribe(
        (response) => {
          resolve(response);
        },
        (error) => {
          this.messageService.showErrorOfErrorResponse(error, 'بروز خطا! لطفا مجددا تلاش کنید');
          reject(error);
        },
      );
    });
  }

  verifyCard(cardCheckResponse: CardCheckResponse, amount: number): Promise<any> {
    let srcCard: CardDefinition = {};
    let destCard: CardDefinition = {};

    const sourceStoredCard = this.sourceStoredCard.getValue();
    const sourceCardProfile = this.c2cSourceCardService.sourceCardProfile.getValue();
    const destStoredCard = this.destStoredCard.getValue();
    const destCardProfile = this.destCardProfile.getValue();

    srcCard = {
      prefix: sourceStoredCard ? getCardPanPrefix(sourceStoredCard.pan) : getCardPanPrefix(sourceCardProfile?.pan as string),
      postfix: sourceStoredCard ? getCardPanPostfix(sourceStoredCard.pan) : getCardPanPostfix(sourceCardProfile?.pan as string),
      cardIndex: sourceStoredCard ? sourceStoredCard.cardIndex : sourceCardProfile?.cardIndex,
      cardProfile: sourceStoredCard ? convertStoredCardToCardProfile(sourceStoredCard) : (sourceCardProfile ?? undefined),
      pan: sourceStoredCard ? sourceStoredCard.pan : sourceCardProfile?.pan,
    };

    destCard = {
      prefix: destStoredCard ? getCardPanPrefix(destStoredCard.pan) : getCardPanPrefix(destCardProfile?.pan as string),
      postfix: destStoredCard ? getCardPanPostfix(destStoredCard.pan) : getCardPanPostfix(destCardProfile?.pan as string),
      cardIndex: destStoredCard ? destStoredCard.cardIndex : destCardProfile?.cardIndex,
      cardProfile: destStoredCard ? convertStoredCardToCardProfile(destStoredCard) : (destCardProfile ?? undefined),
      pan: destStoredCard ? destStoredCard.pan : destCardProfile?.pan,
    };

    const cardVerifyData: CardVerifyData = {
      cardCheckResponse,
      srcCard,
      destCard,
      amount,
    };

    return new Promise((resolve, reject) => {
      this.cardVerificationService.verifyCard(cardVerifyData).then(
        (result) => {
          resolve(result);
        },
        (error) => {
          reject(error);
          this.messageService.showErrorOfErrorResponse(error);
        },
      );
    });
  }

  editCard(card: BankCard, type: string, cardZone: CardZonesEnum = 0): Promise<any> {
    return new Promise((resolve, reject) => {
      this.bottomSheetService.openBottomSheet(CardEditBottomSheetComponent, { card, type }, { noPadding: true });
      const cardEditSub = this.bottomSheetService.onClose.subscribe(() => {
        cardEditSub.unsubscribe();
        const result = this.bottomSheetService.outputData();
        // In case of changing the expiration date, it is necessary to send cardZone to register the change in shaparak
        const strictMode = result?.expireDate !== card?.expireDate;
        this.strictModeEditCard.next(strictMode);
        if (result && result.confirmed) {
          if (result?.alias?.trim() === card?.alias?.trim() && !strictMode) {
            return;
          }
          const request = {
            cardIndex: card.cardIndex,
            alias: result.alias,
            pinned: card.pinned,
            expireDate: result.expireDate,
            callbackUrl: this.shaparakService.shaparakConfig.getValue()?.callbackUrl || '',
          };
          this.bankCardApiService.editCard(request, strictMode ? cardZone : undefined).subscribe(
            (response) => {
              resolve(response);
              this.messageService.showSuccessMessage(response.result.message);
            },
            (e) => {
              reject(e);
              if (e?.result?.message) {
                this.messageService.showErrorOfErrorResponse(e);
              }
            },
          );
        }
      });
    });
  }

  // ------------------sdk----------------------

  sendDynamicPass(): Promise<any> {
    const bankCode = this.sourceStoredCard.getValue()
      ? this.sourceStoredCard.getValue()?.bankCode
      : this.c2cSourceCardService.sourceCardProfile.getValue()?.bankCode;
    return new Promise<any>((resolve, reject) => {
      this.bankCardApiService.getBankClientConfig(bankCode as string).subscribe((response) => {
        const request: DynamicPasswordRequest = {
          certFile: response.certFileName,
          amount: Number(this.amount.getValue()),
          pan: this.makeCardDtoForOtp(
            this.sourceStoredCard.getValue() as BankCard,
            this.c2cSourceCardService.sourceCardProfile.getValue() as CardProfile,
          ),
          targetPan: this.makeCardDtoForOtp(
            this.destStoredCard.getValue() as BankCard,
            this.destCardProfile.getValue() as CardProfile,
            response.certFile,
          ),
          transactionType: CARD_TRANSACTION_TYPES.CARD_TO_CARD,
        };

        this.c2cApiService.requestDynamicPassword(request, response.timeout).subscribe(
          (res) => {
            resolve(res);
          },
          (e) => {
            reject(e);
            this.messageService.showErrorOfErrorResponse(e, 'ارتباط برقرار نشد، لطفا تا لحظاتی دیگر مجددا تلاش کنید');
          },
        );
      });
    });
  }

  cardTransfer(data: CardDetailInterface): Promise<PaymentResultInterface> {
    return new Promise((resolve, reject) => {
      this.bankCardApiService.getCertFileByUrl(this.sourceBank.getValue()?.xferCertFileUrl as string).subscribe(
        (certText) => {
          const encrypt = new JSEncrypt();
          encrypt.setPublicKey(certText);

          // encrypt PIN, CVV2, and Destination card with
          // the certificate of the "source" bank

          let encryptedDto = '';
          let destinationValue = '';
          let destType: CardType;

          const dto: CardPin = {
            cvv2: convertNonEnglishDigits(data.cvv2),
            pin: convertNonEnglishDigits(data.password),
          };
          encryptedDto = encrypt.encrypt(JSON.stringify(dto)) as string;

          if (this.destStoredCard.getValue()) {
            destType = CardType.INDEX;
            destinationValue = this.destStoredCard.getValue()?.cardIndex || '';
          } else {
            destType = CardType.ENCRYPTED;
            destinationValue = encrypt.encrypt(this.destCardProfile.getValue()?.pan || '') as string;
          }
          this.sendTransferRequest(data, encryptedDto, destinationValue, destType).then(
            (result) => {
              resolve(result);
            },
            (e) => {
              reject(e);
            },
          );
        },
        (e) => {
          reject(e);
          this.messageService.showErrorMessage('بروز خطا. لطفا مجددا تلاش کنید');
        },
      );
    });
  }

  refreshSourceCards(): void {
    this.getSourceCardsList(this.getAllBanksCodes(this.allBanks.getValue()));
  }

  refreshDestinationCards(): void {
    this.destStoredCardsList.next(this.destStoredCardsListBeforeDelete.getValue());
  }

  /**
   * user has requested to delete the card,
   * as it is an undoable action, remove
   * from local data first, then after
   * disposal of snackBar, call API
   * to REALLY delete the card
   */
  requestToDeleteCard(card: BankCard, type: 'source' | 'destination') {
    // show confirmation for deleting related repeatedTransactions

    const txn = this.relatedRecommendations(this.recommendationTransactionsList.getValue()?.recommendations || [], card.cardIndex);
    if (txn.length > 0) {
      this.bottomSheetService.openBottomSheet(IndirectDeletingRecommendationComponent, { recommendations: txn });
      const indirectDeletingSub = this.bottomSheetService.onClose.subscribe(() => {
        const result = this.bottomSheetService.outputData();
        if (result && result.confirmed) {
          this.waitToDeleteCard(card, type);
        }
        indirectDeletingSub.unsubscribe();
      });
    } else {
      this.waitToDeleteCard(card, type);
    }
  }

  waitToDeleteCard(card: BankCard, type: 'source' | 'destination') {
    if (type === 'source') {
      this.removeSourceCardFromLocalData(card);
    }
    if (type === 'destination') {
      this.removeDestCardFromLocalData(card);
    }
    //todo fix this part after updating messageService
    this.messageService.showSuccessMessage('کارت بانکی حذف شد');
    this.deleteCard(card, type);
    // const ref = this.messageService.showMessage('کارت بانکی حذف شد', 'بازگرداندن', 5000);
    // let undoClicked = false;
    // ref.onAction().subscribe(() => {
    //   undoClicked = true;
    //   if (type === 'source') {
    //     this.refreshSourceCards();
    //   }
    //   if (type === 'destination') {
    //     this.refreshDestinationCards();
    //   }
    // });
    // ref.afterDismissed().subscribe(() => {
    //   if (!undoClicked) {
    //     this.deleteCard(card, type);
    //   }
    // });
  }

  deleteCard(card: BankCard, type: 'source' | 'destination') {
    // when you delete a stored card, you must delete the repeatedTransactions associated with it
    this.bankCardApiService.deleteCard(card.cardIndex).subscribe(
      () => {
        if (type === 'source') {
          this.refreshSourceCards();
        }
        if (type === 'destination') {
          this.destStoredCardsListBeforeDelete.next([]);
          this.destStoredCardsList.next([]);
          this.getDestinationCardsList();
        }
        this.getRecommendation();
      },
      (e) => {
        this.messageService.showErrorOfErrorResponse(e);
      },
    );
  }

  getRecommendation() {
    this.c2cApiService.getRecommendation().subscribe((response) => {
      this.recommendationTransactionsList.next(response);
    });
  }

  getRecommendationConfig() {
    this.c2cApiService.getRecommendationConfig().subscribe((response) => {
      this.recommendationConfig.next(response);
    });
  }

  relatedRecommendations(recommendations: Recommendation[], cardIndex: string) {
    return recommendations.filter((i) =>
      i.info.find((ii) => {
        return ii.value === cardIndex;
      }),
    );
  }

  storeCardNeedsToVerifyByShaparak(card: BankCard): boolean {
    return card.externalRegistrationMode !== CardRegistration.IGNORE;
  }

  amountConfig(): Promise<CardPaymentConfigResponse> {
    return new Promise((resolve, reject) => {
      if (this.cardPaymentConfig.getValue()) {
        resolve(this.cardPaymentConfig.getValue() as CardPaymentConfigResponse);
        return;
      }
      this.bankCardApiService.getCertFileByUrl(this.sourceBank.getValue()?.xferCertFileUrl || '').subscribe(async (Certificate) => {
        const encryption = new JSEncrypt();
        encryption.setPublicKey(Certificate);

        const pan =
          (this.sourceStoredCard.getValue()
            ? this.sourceStoredCard.getValue()?.pan
            : this.c2cSourceCardService.sourceCardProfile.getValue()?.pan) || '';
        const prefix = getCardPanPrefix(pan);
        const postfix = getCardPanPostfix(pan);

        const destStoredCard = this.destStoredCard.getValue();
        const destCardProfile = this.destCardProfile.getValue();
        const targetPan = (destStoredCard ? destStoredCard.pan : destCardProfile?.pan) || '';
        const destPrefix = getCardPanPrefix(targetPan);
        const destPostfix = getCardPanPostfix(targetPan);

        let destValue = '';
        let destType: CardType;
        if (this.destStoredCard.getValue()) {
          destType = CardType.INDEX;
          destValue = this.destStoredCard.getValue()?.cardIndex || '';
        } else {
          destType = CardType.ENCRYPTED;
          destValue = encryption.encrypt(this.destCardProfile.getValue()?.pan || '') as string;
        }
        let encryptedSourceCardNumber = '';
        const clientTraceCode = await this.c2cEcdhService.generateClientTraceCode();
        const data: CardPaymentConfig = {
          trace: clientTraceCode,
          bankCode: this.sourceBank.getValue()?.code || '',
          pan: {
            expireDate: '',
            type: this.sourceStoredCard.getValue() ? CardType.INDEX : CardType.ENCRYPTED,
            prefix,
            postfix,
            value: '',
          },
          targetPan: {
            type: destType,
            prefix: destPrefix,
            postfix: destPostfix,
            value: destValue,
          },
        };
        if (this.sourceStoredCard.getValue()) {
          data.pan.value = this.sourceStoredCard.getValue()?.cardIndex || '';
          this.getAmountConfig(data).then(
            (config) => {
              resolve(config);
            },
            (e) => {
              reject(e);
            },
          );
        } else {
          this.c2cSourceCardService.getVaultCertFile().then((certText) => {
            const encrypt = new JSEncrypt();
            encrypt.setPublicKey(certText);
            encryptedSourceCardNumber = encrypt.encrypt(pan) as string;
            data.pan.value = encryptedSourceCardNumber;
            this.getAmountConfig(data).then(
              (config) => {
                resolve(config);
              },
              (e) => {
                reject(e);
              },
            );
          });
        }
      });
    });
  }

  getAmountConfig(data: CardPaymentConfig): Promise<CardPaymentConfigResponse> {
    return new Promise((resolve, reject) => {
      this.c2cApiService.getAmountConfig(data).subscribe(
        (config) => {
          this.cardPaymentConfig.next(config);
          resolve(config);
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  storeDataForDynamicPassword(countdownSeconds: number) {
    // Dynamic password requirements
    const srcCardIndex =
      (this.sourceStoredCard.getValue()
        ? this.sourceStoredCard.getValue()?.cardIndex
        : this.c2cSourceCardService.sourceCardProfile.getValue()?.cardIndex) || '';
    const desCardIndex = this.destStoredCard.getValue()
      ? this.destStoredCard.getValue()?.cardIndex
      : this.destCardProfile.getValue()?.cardIndex;
    const value = this.amount.getValue();

    const data = {
      srcCardIndex,
      desCardIndex,
      value,
      countdownSeconds,
      startTime: +new Date(),
    };
    this.storageService.setCardHistory(srcCardIndex, data);
  }

  clear() {
    this.allBanks.next([]);
    this.sourceBank.next(null);
    this.c2cSourceCardService.sourceCardProfile.next(null);
    this.sourceStoredCardsList.next([]);
    this.sourceStoredCard.next(null);
    this.sourceCardsLoaded.next(SOURCE_CARD_LOAD_STATUS.LOADING);
    this.destBank.next(null);
    this.destCardProfile.next(null);
    this.destStoredCardsList.next([]);
    this.destStoredCard.next(null);
    this.addNewCardUI.next(null);
    this.defaultCardUI.next(null);
    this.amount.next('');
    this.note.next('');
    this.paymentResult.next(null);
    this.recommendationTransactionsList.next(null);
    this.repeatTransaction.next(false);
    this.transactionMessage.next('');
    this.recommendationConfig.next(null);
    this.fromRepeatTransaction.next(null);
    this.destCardNumber.next('');
  }

  private makeCardDtoForOtp(storedCard: BankCard, cardProfile: CardProfile, certFile?: string): CardPanDto {
    const prefix = storedCard ? storedCard.prefix : getCardPanPrefix(cardProfile?.pan);
    const postfix = storedCard ? storedCard.postfix : getCardPanPostfix(cardProfile?.pan);
    let value = (storedCard ? storedCard.cardIndex : cardProfile.cardIndex) || '';
    let type = CardType.INDEX;

    if (!value && certFile) {
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(certFile);
      value = encrypt.encrypt(cardProfile?.pan) as string;
      type = CardType.ENCRYPTED;
    }
    return {
      expireDate: storedCard ? storedCard.expireDate : cardProfile.expirationDate,
      type,
      value,
      prefix,
      postfix,
    };
  }

  private async sendTransferRequest(
    data: CardDetailInterface,
    encryptedPin: string,
    destCardNumber = '',
    destType: CardType,
  ): Promise<PaymentResultInterface> {
    const srcPrefix = this.sourceStoredCard.getValue()
      ? this.sourceStoredCard.getValue()?.prefix
      : getCardPanPrefix(this.c2cSourceCardService.sourceCardProfile.getValue()?.pan || '');
    const srcPostfix = this.sourceStoredCard.getValue()
      ? this.sourceStoredCard.getValue()?.postfix
      : getCardPanPostfix(this.c2cSourceCardService.sourceCardProfile.getValue()?.pan || '');
    const destPrefix =
      (this.destStoredCard.getValue()
        ? this.destStoredCard.getValue()?.prefix
        : getCardPanPrefix(this.destCardProfile.getValue()?.pan || '')) || '';
    const destPostfix = this.destStoredCard.getValue()
      ? this.destStoredCard.getValue()?.postfix
      : getCardPanPostfix(this.destCardProfile.getValue()?.pan || '');

    let request: CardToCardRequest = {
      amount: Number(this.amount.getValue()),
      bankCode: this.sourceBank.getValue()?.code || '',
      certFile: this.sourceBank.getValue()?.xferCert || '',
      message: this.transactionMessage.getValue() ? this.transactionMessage.getValue() : '',
      destFullName:
        (this.destStoredCard.getValue() ? this.destStoredCard.getValue()?.cardOwner : this.destCardProfile.getValue()?.cardHolder) || '',
      saveDestination: false, // always false
      saveSource: false, // always false
      saveRecommendation: this.repeatTransaction.getValue(),
      encryptedPinDto: encryptedPin,
      source: {
        expireDate: data.expirationDate,
        type: Number(CardType.INDEX),
        prefix: srcPrefix || '',
        postfix: srcPostfix || '',
        value:
          (this.sourceStoredCard.getValue()
            ? this.sourceStoredCard.getValue()?.cardIndex
            : this.c2cSourceCardService.sourceCardProfile.getValue()?.cardIndex) || '',
      },
      destination: {
        type: Number(destType),
        prefix: destPrefix || '',
        postfix: destPostfix || '',
        value: destCardNumber || '',
      },
    };
    // check and push fingerPrint
    if (this.serverTraceCode.getValue()) {
      const { publicKey, randomNumber } = await this.c2cEcdhService.DecodeServerTraceCode(this.serverTraceCode.getValue());
      const hmacInput = destPrefix + destPostfix + request.amount + randomNumber + srcPrefix + srcPostfix + this.storageService.getUserId();
      const expireDate = request.source.expireDate;
      const hmacHash = await this.c2cEcdhService.generateFingerPrint(publicKey, hmacInput, expireDate, request.encryptedPinDto);
      request = { ...request, fingerprint: hmacHash };
    }
    // send transfer request
    return new Promise((resolve, reject) => {
      this.c2cApiService.cardToCardTransfer(request).subscribe(
        (response) => {
          resolve(response);
        },
        (e) => {
          reject(e);
          this.messageService.showErrorOfErrorResponse(e);
        },
      );
    });
  }
}
